/* eslint-disable */
import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl';

import 'mapbox-gl/dist/mapbox-gl.css';
import 'assets/styles/map.scss';

import turfBbox from '@turf/bbox';
import axios from 'axios';
import proj4 from 'proj4';
import { featureCollection } from '@turf/helpers';
import { getHeaders } from 'utils/Headers';
import BmIcon from 'assets/images/icons/bmPng.png';
import LmIcon from 'assets/images/icons/lmPng.png';
import EvidenceImageIcon from 'assets/images/icons/Photo.png';
import EvidenceIcon from 'assets/images/icons/evidence-icon1.png';
import IndirectEvidenceIcon from 'assets/images/icons/evidence-icon.png';
import { useMapbox } from 'utils/MapboxContext';
// import { useAzure } from 'utils/AzureContext';
import { MAP_SWITCHERS_PANEL_WIDTH } from 'consts/mapControls';
import { SwitherComponent } from './Swither';

proj4.defs('EPSG:3857', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs');
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');

const isValidCoord = (coord) => {
  if (!coord || !Array.isArray(coord) || coord.length < 2) return false;
  const lng = Number(coord[0]);
  const lat = Number(coord[1]);
  if (typeof lng !== 'number' || typeof lat !== 'number') return false;
  if (lng !== lng || lat !== lat) return false;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  return Math.abs(lng) <= 180 && Math.abs(lat) <= 90;
};

const countCoords = (geom) => {
  if (!geom || !geom.coordinates) return 0;
  const c = geom.coordinates;
  if (geom.type === 'Point') return 1;
  if (geom.type === 'LineString') return c.length;
  if (geom.type === 'Polygon') return c.reduce((sum, ring) => sum + (Array.isArray(ring) ? ring.length : 0), 0);
  if (geom.type === 'MultiPolygon') return c.reduce((sum, poly) => sum + (Array.isArray(poly) ? poly.reduce((s, ring) => s + (Array.isArray(ring) ? ring.length : 0), 0) : 0), 0);
  return 0;
};

const MAX_COORDS_PER_FEATURE = 100000;
const DEFAULT_POPUP_VALUE = 'Unknown';

const readProperty = (properties = {}, keys = []) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    const value = properties?.[key];
    if (value !== null && value !== undefined && `${value}`.trim() !== '') {
      return value;
    }
  }
  return DEFAULT_POPUP_VALUE;
};

const buildImsmaPopupData = (properties = {}) => {
  const imsmaId = readProperty(properties, ['imsma_id', 'imsmaId', 'IMSMA ID', 'imsma']);
  const minefieldOrBac = readProperty(properties, ['minefield_bac', 'minefield_or_bac', 'minefield', 'bac', 'Minefield/BAC']);
  const shaOrCha = readProperty(properties, ['hazard_area_type', 'sha_cha', 'sha_or_cha', 'SHA/CHA']);

  return { imsmaId, minefieldOrBac, shaOrCha };
};

const getPopupValueClassName = (value) => (
  value === DEFAULT_POPUP_VALUE
    ? 'imsma-compact-popup__value imsma-compact-popup__value--unknown'
    : 'imsma-compact-popup__value'
);

export const validateGeoData = (data) => {
  if (!data || !data.features) return data;

  const validFeatures = data.features.filter((feature) => {
    if (!feature.geometry || !feature.geometry.coordinates) return false;
    if (countCoords(feature.geometry) > MAX_COORDS_PER_FEATURE) return false;

    if (feature.geometry.type === 'Point') {
      return isValidCoord(feature.geometry.coordinates);
    }

    if (feature.geometry.type === 'LineString') {
      return feature.geometry.coordinates.every(isValidCoord);
    }

    if (feature.geometry.type === 'Polygon') {
      return feature.geometry.coordinates.every((ring) =>
        Array.isArray(ring) && ring.every(isValidCoord)
      );
    }

    if (feature.geometry.type === 'MultiPolygon') {
      return feature.geometry.coordinates.every((polygon) =>
        Array.isArray(polygon) &&
        polygon.every((ring) => Array.isArray(ring) && ring.every(isValidCoord))
      );
    }

    return true;
  });

  return {
    ...data,
    features: validFeatures
  };
};

export const Map = React.memo((props) => {
  const { token } = useMapbox();
  // const { azureApiKey } = useAzure();
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);

  const [googleCheckbox, setGoogleCheckbox] = useState(false);
  const [mapboxStyleCheckbox, setMapboxStyleCheckbox] = useState(false);
  const [compactPopupData, setCompactPopupData] = useState(null);
  const [isCompactPopupVisible, setIsCompactPopupVisible] = useState(false);
  const userClosedCompactPopupRef = useRef(false);
  const lastAutoPopupKeyRef = useRef(null);
  const compactPopupEnabled = props.showImsmaCompactPopup !== false;

  // const tileToQuadKey = (x, y, z) => {
  //   let quadkey = '';
  //   for (let i = z; i > 0; i--) {
  //     let digit = 0;
  //     const mask = 1 << (i - 1);
  //     if ((x & mask) !== 0) digit += 1;
  //     if ((y & mask) !== 0) digit += 2;
  //     quadkey += digit;
  //   }
  //   return quadkey;
  // };



  // const getAzureTilesUrl = () => {
  //   if (azureApiKey) {
  //     return [`https://atlas.microsoft.com/map/tile?api-version=2022-08-01&tilesetId=microsoft.base.hybrid&zoom={z}&x={x}&y={y}&tileSize=256&subscription-key=${azureApiKey}`];
  //   } else {
  //     // Fallback до OpenStreetMap tiles для тестування
  //     return ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'];
  //   }
  // };

  function transformMaxarTilesUrl(url) {
    const coordsParam = url.split('?')[1].split('&').find((param) => param.startsWith('coords='));

    if (coordsParam) {
      const coords = coordsParam.substring('coords='.length).split(',');
      if (coords.length < 4) {
        console.warn('Invalid coords parameter in transformMaxarTilesUrl:', coords);
        return url;
      }
      const [west, south, east, north] = coords.map(parseFloat);

      const transformedCoordinates1 = proj4('EPSG:3857', 'EPSG:4326', [west, south]);
      const transformedCoordinates2 = proj4('EPSG:3857', 'EPSG:4326', [east, north]);

      const coordsNew = `${transformedCoordinates1[0]},
                ${transformedCoordinates1[1]},
                ${transformedCoordinates2[0]},
                ${transformedCoordinates2[1]}`;

      const updatedUrl = url.replace(coordsParam, `coords=${coordsNew}`);

      return updatedUrl;
    }

    return url;
  }

  function transformRequest(url, resourceType) {
    if (url.includes('maxar-tiles')) {
      const updatedUrl = transformMaxarTilesUrl(url);

      return {
        url: updatedUrl,
        headers: { Authorization: getHeaders() },
        credentials: 'include',
      };
    }

    if (resourceType === 'Tile' && url.indexOf(axios.defaults.baseURL) > -1) {
      return {
        url,
        headers: { Authorization: getHeaders() },
        credentials: 'include',
      };
    }
    return false;
  }


  useEffect(() => {
    mapboxgl.accessToken = token;
    if (!map && token) {
      const newMap = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/light-v10',
        center: [32, 49],
        zoom: 3.5,
        transformRequest: (url, resourceType) => transformRequest(url, resourceType),
      });

      newMap.on('load', () => {
        setMap(newMap);
        props.setMap(newMap);
      });
    }
  }, [props.setMap, map, token]);

  useEffect(() => {
    const mapInstance = map;
    const setMapRef = props.setMap;
    return () => {
      if (mapInstance) {
        setMapRef(null);
        mapInstance.remove();
      }
    };
  }, [map]);

  useEffect(() => {
    if (map && !map?.getSource('wms-google')) {
      map.addSource('wms-google', {
        type: 'raster',
        tiles: ['https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'],
        tileSize: 256,
      });

      map.addLayer({
        id: 'wms-google-layer',
        type: 'raster',
        source: 'wms-google',
        layout: {
          visibility: 'none',
        },
      });
    }

    if (map && !map.getSource('mapbox-satellite')) {
      map.addSource('mapbox-satellite', {
        type: 'raster',
        url: 'mapbox://mapbox.satellite',
      });
      map.addLayer({
        id: 'mapbox-satellite-layer',
        type: 'raster',
        source: 'mapbox-satellite',
        layout: {
          visibility: 'none',
        },
      });
    }
  }, [map]);

  //   if (map) {
  //     if (!map.getSource('azure-layer')) {
  //       map.addSource('azure-layer', {
  //         type: 'raster',
  //         tiles: getAzureTilesUrl(),
  //         tileSize: 256,
  //       });

  //       map.addLayer({
  //         id: 'azure-layer',
  //         type: 'raster',
  //         source: 'azure-layer',
  //         layout: {
  //           visibility: 'none',
  //         },
  //       });

  //       map.on('error', (e) => {
  //         if (e.sourceId === 'azure-layer') {
  //           console.warn('Azure Maps layer error:', e.error);
  //           if (azureApiKey && e.error?.status === 401) {
  //             console.error('Azure Maps API key is invalid or expired');
  //           } else if (!azureApiKey) {
  //             console.info('No Azure Maps API key found, using OpenStreetMap fallback tiles');
  //           }
  //         }
  //       });
  //           } else {
  //       const existingSource = map.getSource('azure-layer');
  //       if (existingSource) {
  //         if (map.getLayer('azure-layer')) {
  //           map.removeLayer('azure-layer');
  //         }
  //         map.removeSource('azure-layer');

  //         map.addSource('azure-layer', {
  //           type: 'raster',
  //           tiles: getAzureTilesUrl(),
  //           tileSize: 256,
  //         });

  //         map.addLayer({
  //           id: 'azure-layer',
  //           type: 'raster',
  //           source: 'azure-layer',
  //           layout: {
  //             visibility: 'none',
  //           },
  //         });
  //       }
  //     }
  //   }
  // }, [map]);

  const updateSource = (source, data) => {
    const validatedData = validateGeoData(data);
    map.getSource(source).setData(validatedData);

    if (!validatedData?.features?.length) {
      return;
    }

    if (Array.isArray(validatedData?.features[0]) && validatedData?.features[0]?.length === 0) {
      return;
    }

    const firstFeature = validatedData?.features[0];
    if (!firstFeature?.geometry?.coordinates?.length) {
      return;
    }

    try {
      const bbox = turfBbox(validatedData);
      if (bbox.some((coord) => !Number.isFinite(coord) || Math.abs(coord) > 180)) {
        console.warn('Invalid bbox coordinates, skipping fitBounds', bbox);
        return;
      }
      map.fitBounds(bbox);
    } catch (error) {
      console.error('Error calculating bbox:', error);
    }
  };

  const setLmGeojson = (data) => {
    const validatedData = validateGeoData(data);
    map.addSource('lm', {
      type: 'geojson',
      data: validatedData,
      generateId: true,
    });

    [{ 'lm-icon': LmIcon }].forEach((icon) => {
      const iconPath = Object.values(icon)[0];
      const iconName = Object.keys(icon)[0];

      if (!iconPath) {
        console.warn(`Icon path is undefined for ${iconName}`);
        return;
      }

      map.loadImage(iconPath, (error, image) => {
        if (error) {
          console.error(`Error loading ${iconName}:`, error);
          return;
        }
        map.addImage(iconName, image);
      });
    });

    map.addLayer({
      id: 'lm-layer',
      type: 'symbol',
      source: 'lm',
      layout: {
        'icon-image': 'lm-icon',
        'icon-size': 1,
        'icon-offset': [0, 0],
        'icon-allow-overlap': true,
      },
    });
  };

  const setBmGeojson = (data) => {
    const validatedData = validateGeoData(data);
    map.addSource('bm', {
      type: 'geojson',
      data: validatedData,
      generateId: true,
    });

    [{ 'bm-icon': BmIcon }].forEach((icon) => {
      const iconPath = Object.values(icon)[0];
      const iconName = Object.keys(icon)[0];

      if (!iconPath) {
        console.warn(`Icon path is undefined for ${iconName}`);
        return;
      }

      map.loadImage(iconPath, (error, image) => {
        if (error) {
          console.error(`Error loading ${iconName}:`, error);
          return;
        }
        map.addImage(iconName, image);
      });
    });

    map.addLayer({
      id: 'bm-layer',
      type: 'symbol',
      source: 'bm',
      layout: {
        'icon-image': 'bm-icon',
        'icon-size': 1,
        'icon-offset': [0, 0],
        'icon-allow-overlap': true,
      },
    });
  };
  const setTurnPointsGeojson = (data) => {
    const validatedData = validateGeoData(data);

    const getColor = () => {
      if (props.hazardAreaType) {
        return [
          'match',
          props.hazardAreaType,
          'I3T|SHA', '#ff0000',
          '3T|CHA', '#ffa500',
          '#808080',
        ];
      } if (props.typeInFeature) {
        return [
          'match',
          ['get', 'hazard_area_type'],
          'I3T|SHA', '#ff0000',
          '3T|CHA', '#ffa500',
          '#808080',
        ];
      }
      return ['get', 'color'];
    };
    map.addSource('turnPointsSource', {
      type: 'geojson',
      data: validatedData,
      generateId: true,
    });

    map.addLayer({
      id: 'turnPointsPolygon-layer',
      type: 'fill',
      source: 'turnPointsSource',
      paint: {
        'fill-color': getColor(),
        'fill-opacity': 0.5,
      },
      filter: ['==', '$type', 'Polygon'],
    });

    map.addLayer({
      id: 'turnPoints-layer',
      type: 'circle',
      source: 'turnPointsSource',
      paint: {
        'circle-radius': 4,
        'circle-color': getColor(),
      },
      filter: ['==', '$type', 'Point'],
    });

    map.addLayer({
      id: 'turnPoints-text-layer',
      type: 'symbol',
      source: 'turnPointsSource',
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 12,
        'text-offset': [0, -1.5],
      },
      filter: ['==', '$type', 'Point'],
    });
    const filteredPolygon = validatedData.features?.filter((feature) => feature.geometry?.type === 'Polygon');
    if (!filteredPolygon?.length || !filteredPolygon[0]?.geometry?.coordinates?.length) return;

    try {
    const bbox = turfBbox(featureCollection(filteredPolygon));

      if (bbox.some((coord) => !Number.isFinite(coord) || Math.abs(coord) > 180)) {
        console.warn('Invalid bbox coordinates in setTurnPointsGeojson, skipping fitBounds', bbox);
        return;
      }

    map.fitBounds(bbox);
    } catch (error) {
      console.error('Error calculating bbox in setTurnPointsGeojson:', error);
    }
  };

  const setEvidenceGeojson = (data) => {
    const validatedData = validateGeoData(data);
    validatedData.features.forEach((feature, index) => {
      feature.properties.text = `4.2.${index + 1}`;
      feature.properties.photoLength = props.evidences?.direct_evidences.items[index]
        ?.photo?.length;
    });
    map.addSource('evidence', {
      type: 'geojson',
      data: validatedData,
      generateId: true,
    });

    [{ 'evidence-icon': EvidenceIcon },
      { 'evidence-image-icon': EvidenceImageIcon }].forEach((icon) => {
      const iconPath = Object.values(icon)[0];
      const iconName = Object.keys(icon)[0];

      if (!iconPath) {
        console.warn(`Icon path is undefined for ${iconName}`);
        return;
      }

      map.loadImage(iconPath, (error, image) => {
        if (error) {
          console.error(`Error loading ${iconName}:`, error);
          return;
        }
        map.addImage(iconName, image);
      });
    });

    map.addLayer({
      id: 'evidence-layer',
      type: 'symbol',
      source: 'evidence',
      layout: {
        'icon-image': 'evidence-icon',
        'icon-size': 0.05,
        'icon-offset': [0, 0],
        'icon-allow-overlap': true,
      },
    });

    map.addLayer({
      id: 'evidence-text-layer',
      type: 'symbol',
      source: 'evidence',
      layout: {
        'text-field': ['get', 'text'],
        'text-size': 14,
        'text-offset': [0, -1.5],
      },
    });
    map.addLayer({
      id: 'evidence-image-layer',
      type: 'symbol',
      source: 'evidence',
      layout: {
        'icon-image': 'evidence-image-icon',
        'icon-size': 0.5,
        'icon-offset': [50, -40],
        'icon-allow-overlap': true,
      },
      filter: ['>', 'photoLength', 0],
    });
  };

  const setIndirectEvidenceGeojson = (data) => {
    const validatedData = validateGeoData(data);
    validatedData.features.forEach((feature, index) => {
      feature.properties.text = `4.5.${index + 1}`;
      feature.properties.photoLength = props.evidences?.indirect_evidences.items[index]
        ?.photo?.length;
    });
    map.addSource('indirect-evidence', {
      type: 'geojson',
      data: validatedData,
      generateId: true,
    });

    [{ 'indirect-evidence-icon': IndirectEvidenceIcon },
      { 'evidence-image-icon': EvidenceImageIcon }].forEach((icon) => {
      const iconPath = Object.values(icon)[0];
      const iconName = Object.keys(icon)[0];

      if (!iconPath) {
        console.warn(`Icon path is undefined for ${iconName}`);
        return;
      }

      map.loadImage(iconPath, (error, image) => {
        if (error) {
          console.error(`Error loading ${iconName}:`, error);
          return;
        }
        map.addImage(iconName, image);
      });
    });

    map.addLayer({
      id: 'indirect-evidence-layer',
      type: 'symbol',
      source: 'indirect-evidence',
      layout: {
        'icon-image': 'indirect-evidence-icon',
        'icon-size': 0.03,
        'icon-offset': [0, 0],
        'icon-allow-overlap': true,
      },
    });

    map.addLayer({
      id: 'indirect-evidence-text-layer',
      type: 'symbol',
      source: 'indirect-evidence',
      layout: {
        'text-field': ['get', 'text'],
        'text-size': 14,
        'text-offset': [0, -1.5],
      },
    });

    map.addLayer({
      id: 'indirect-evidence-image-layer',
      type: 'symbol',
      source: 'indirect-evidence',
      layout: {
        'icon-image': 'evidence-image-icon',
        'icon-size': 0.5,
        'icon-offset': [50, -40],
        'icon-allow-overlap': true,
      },
      filter: ['>', 'photoLength', 0],
    });
  };

  const setHaReportsPolygons = (data) => {
    const validatedData = validateGeoData(data);

    map.addSource('ha-reports-polygons', {
      type: 'geojson',
      data: validatedData,
      generateId: true,
    });

    if (!map.getLayer('ha-reports-polygons-outline-layer')) {
      map.addLayer({
        id: 'ha-reports-polygons-outline-layer',
        type: 'line',
        source: 'ha-reports-polygons',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 2,
        },
        filter: ['==', '$type', 'Polygon'],
      });
    }

    if (!map.getLayer('ha-reports-polygons-imsma-hitbox-layer')) {
      map.addLayer({
        id: 'ha-reports-polygons-imsma-hitbox-layer',
        type: 'fill',
        source: 'ha-reports-polygons',
        paint: {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
        filter: [
          'all',
          ['==', '$type', 'Polygon'],
          ['==', '__imsmaClickable', true],
        ],
      });
    }

    if (validatedData?.features?.length > 0) {
      try {
        const bbox = turfBbox(validatedData);
        if (bbox.some((coord) => !Number.isFinite(coord) || Math.abs(coord) > 180)) {
          console.warn('Invalid bbox coordinates for HA Reports polygons, skipping fitBounds', bbox);
          return;
        }

        map.fitBounds(bbox, {
          padding: 50,
          maxZoom: 10
        });
      } catch (error) {
        console.error('Error calculating bbox for HA Reports polygons:', error);
      }
    }
  };

  const showCompactPolygonPopup = (feature) => {
    if (!compactPopupEnabled) return;
    if (!feature) return;
    setCompactPopupData(buildImsmaPopupData(feature.properties));
    setIsCompactPopupVisible(true);
    userClosedCompactPopupRef.current = false;
  };

  useEffect(() => {
    if (map && props.lmGeojson) {
      const data = props.lmGeojson;
      if (map.getSource('lm')) {
        updateSource('lm', data);
      } else {
        setLmGeojson(data);
      }
    }
  }, [map, props.lmGeojson]);

  useEffect(() => {
    if (map && props.bmGeojson) {
      const data = props.bmGeojson;
      if (map.getSource('bm')) {
        updateSource('bm', data);
      } else {
        setBmGeojson(data);
      }
    }
  }, [map, props.bmGeojson]);

  useEffect(() => {
    if (map && props.turnPointsPolygon) {
      const data = props.turnPointsPolygon;
      if (map.getSource('turnPointsSource')) {
        updateSource('turnPointsSource', data);
      } else {
        setTurnPointsGeojson(data);
      }
    }
  }, [map, props.turnPointsPolygon]);

  useEffect(() => {
    if (map && props.evidenceGeojson) {
      const data = props.evidenceGeojson;
      if (map.getSource('evidence')) {
        data.features.forEach((feature, index) => {
          feature.properties.text = `4.2.${index + 1}`;
          feature.properties.photoLength = props.evidences?.direct_evidences.items[index]
            ?.photo?.length;
        });
        updateSource('evidence', data);
      } else {
        setEvidenceGeojson(data);
      }
    }
  }, [map, props.evidenceGeojson, props.evidences]);

  useEffect(() => {
    if (map && props.indirectEvidenceGeojson) {
      const data = props.indirectEvidenceGeojson;
      if (map.getSource('indirect-evidence')) {
        data.features.forEach((feature, index) => {
          feature.properties.text = `4.5.${index + 1}`;
          feature.properties.photoLength = props.evidences?.indirect_evidences.items[index]
            ?.photo?.length;
        });
        updateSource('indirect-evidence', data);
      } else {
        setIndirectEvidenceGeojson(data);
      }
    }
  }, [map, props.indirectEvidenceGeojson]);

  useEffect(() => {
    if (map && props?.haReportsPolygons) {
      const data = props?.haReportsPolygons;
      if (map.getSource('ha-reports-polygons')) {
        const validatedData = validateGeoData(data);
        map.getSource('ha-reports-polygons').setData(validatedData);
        if (!map.getLayer('ha-reports-polygons-outline-layer')) {
          map.addLayer({
            id: 'ha-reports-polygons-outline-layer',
            type: 'line',
            source: 'ha-reports-polygons',
            paint: {
              'line-color': ['get', 'color'],
              'line-width': 2,
            },
            filter: ['==', '$type', 'Polygon'],
          });
        }
        if (!map.getLayer('ha-reports-polygons-imsma-hitbox-layer')) {
          map.addLayer({
            id: 'ha-reports-polygons-imsma-hitbox-layer',
            type: 'fill',
            source: 'ha-reports-polygons',
            paint: {
              'fill-color': '#000000',
              'fill-opacity': 0,
            },
            filter: [
              'all',
              ['==', '$type', 'Polygon'],
              ['==', '__imsmaClickable', true],
            ],
          });
        }
      } else {
        setHaReportsPolygons(data);
      }
    }
  }, [map, props?.haReportsPolygons]);

  useEffect(() => {
    if (!compactPopupEnabled) return undefined;
    if (!map) return undefined;

    const getClickableLayers = () => ['ha-reports-polygons-imsma-hitbox-layer']
      .filter((layerId) => Boolean(map.getLayer(layerId)));

    const clickHandler = (event) => {
      const clickableLayers = getClickableLayers();
      if (!clickableLayers.length) return;
      const features = map.queryRenderedFeatures(event.point, { layers: clickableLayers });
      const clickedFeature = features?.[0];
      if (!clickedFeature) return;
      showCompactPolygonPopup(clickedFeature);
    };

    const mouseMoveHandler = (event) => {
      const clickableLayers = getClickableLayers();
      if (!clickableLayers.length) {
        map.getCanvas().style.cursor = '';
        return;
      }
      const features = map.queryRenderedFeatures(event.point, { layers: clickableLayers });
      map.getCanvas().style.cursor = features?.length ? 'pointer' : '';
    };

    const mouseLeaveHandler = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('click', clickHandler);
    map.on('mousemove', mouseMoveHandler);
    map.on('mouseleave', mouseLeaveHandler);

    return () => {
      map.off('click', clickHandler);
      map.off('mousemove', mouseMoveHandler);
      map.off('mouseleave', mouseLeaveHandler);
    };
  }, [compactPopupEnabled, map, props?.haReportsPolygons]);

  useEffect(() => {
    if (!compactPopupEnabled) {
      setIsCompactPopupVisible(false);
      return;
    }
    const hasImsmaPolygons = (props?.haReportsPolygons?.features || []).some((feature) => {
      const isPolygon =
        feature?.geometry?.type === 'Polygon' || feature?.geometry?.type === 'MultiPolygon';
      return isPolygon && feature?.properties?.__imsmaClickable === true;
    });
    if (!hasImsmaPolygons) {
      setIsCompactPopupVisible(false);
      setCompactPopupData(null);
    }
  }, [compactPopupEnabled, props?.haReportsPolygons]);

  // useEffect(() => {
  //   if (map && props.azureLayerEnabled !== undefined) {
  //     handleCheckboxLayer('azure-layer', props.azureLayerEnabled);
  //     if (props.azureLayerEnabled) {
  //       handleCheckboxLayer('wms-google-layer', false);
  //       handleCheckboxLayer('mapbox-satellite-layer', false);
  //       setGoogleCheckbox(false);
  //       setMapboxStyleCheckbox(false);
  //     }
  //   }
  // }, [map, props.azureLayerEnabled]);

  const handleCheckboxLayer = (layer, isChecked) => {
    const visibleProp = isChecked ? 'visible' : 'none';
    map.setLayoutProperty(layer, 'visibility', visibleProp);
  };

  return (
    <div className="map-wrapper">
      {token && (
        <div
          ref={mapContainerRef}
          className={`map-container ${props.height} ${props.width}`}
        />
      )}
      {!token && (
        <div className="map-container error-text flex-center align-center">
          Неможливо завантажити карту
        </div>
      )}
      {props.googleWms && token
        && (
          <div
            className="mapbox-control flex-column-group"
            style={{ width: MAP_SWITCHERS_PANEL_WIDTH, boxSizing: 'border-box' }}
          >
            <SwitherComponent
              labelRight="Satellite (google)"
              checked={googleCheckbox}
              handleSwitcher={(isChecked) => {
                handleCheckboxLayer('wms-google-layer', isChecked);
                handleCheckboxLayer('mapbox-satellite-layer', false);
                // handleCheckboxLayer('azure-layer', false);
                setMapboxStyleCheckbox(false);
                setGoogleCheckbox(isChecked);
                // if (isChecked && props.setAzureLayerEnabled) {
                //   props.setAzureLayerEnabled(false);
                // }
              }}
            />
            <SwitherComponent
              labelRight="Satellite (mapbox)"
              checked={mapboxStyleCheckbox}
              handleSwitcher={(isChecked) => {
                handleCheckboxLayer('wms-google-layer', false);
                handleCheckboxLayer('mapbox-satellite-layer', isChecked);
                // handleCheckboxLayer('azure-layer', false);
                setGoogleCheckbox(false);
                setMapboxStyleCheckbox(isChecked);
                  // if (isChecked && props.setAzureLayerEnabled) {
                  //   props.setAzureLayerEnabled(false);
                  // }
              }}
            />
          </div>
        )}
      {compactPopupEnabled && isCompactPopupVisible && compactPopupData && (
        <div className="imsma-fixed-popup">
          <button
            type="button"
            className="imsma-fixed-popup__close"
            onClick={() => {
              userClosedCompactPopupRef.current = true;
              setIsCompactPopupVisible(false);
            }}
            aria-label="Close popup"
          >
            ×
          </button>
          <div className="imsma-compact-popup">
            <div className="imsma-compact-popup__row">
              <span className="imsma-compact-popup__label">IMSMA ID:</span>
              <span className={getPopupValueClassName(compactPopupData.imsmaId)}>{compactPopupData.imsmaId}</span>
            </div>
            <div className="imsma-compact-popup__row">
              <span className="imsma-compact-popup__label">Minefield/BAC:</span>
              <span className={getPopupValueClassName(compactPopupData.minefieldOrBac)}>{compactPopupData.minefieldOrBac}</span>
            </div>
            <div className="imsma-compact-popup__row">
              <span className="imsma-compact-popup__label">SHA/CHA:</span>
              <span className={getPopupValueClassName(compactPopupData.shaOrCha)}>{compactPopupData.shaOrCha}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
